import React, { useState } from 'react';
import _, { isEmpty } from 'underscore';
import { useDispatch, useSelector } from 'react-redux';
import { BANNER } from '@shoflo/polymer/constants/SourceItems';

import deleteSourceFromScene from 'actions/Broadcast/scene/deleteSourceFromScene';
import replaceSourceInScenes from 'actions/Broadcast/scene/replaceSourceInScenes';
import {
  isMediaSource,
  isPresenterSource,
  isScreenPresenter
} from 'utils/broadcastSourceHelper';
import clearModal from '@shoflo/studio-redux/actions/modal/clearModal/clearModal';
import ModalHeader from 'components/Modal/Header';
import { DELETE_SOURCE_FORM } from 'constants/ModalTypes';
import DeleteSource from 'components/DeleteSource/DeleteSource';
import { StyledTitle, StyledModal } from './DeleteSourceModal.styled';

const DeleteSourceModal = () => {
  const dispatch = useDispatch();

  const [selectedSource, setSelectedSource] = useState(null);

  const {
    isOpen,
    type,
    scenesWithSource,
    sources,
    sourceId,
    deleteParams,
    backgrounds,
    overlays,
    banners
  } = useSelector(
    ({
      modal,
      scenesWithSource,
      broadcastSources,
      backgrounds,
      overlays,
      banners
    }) => ({
      isOpen: modal && modal.type === DELETE_SOURCE_FORM,
      type: modal && modal.type,
      sourceId: modal && modal.initialValue?.sourceId,
      deleteParams: modal && modal.initialValue?.deleteParams,
      scenesWithSource,
      sources: broadcastSources.sources,
      backgrounds,
      overlays,
      banners
    })
  );

  const clear = () => dispatch(clearModal(type));

  const handleSourceSelected = source => {
    setSelectedSource(source);
  };

  const handleSubmit = () => {
    // This is a deep clone approach where scenes we are dealing with moving forward
    // will have no reference to the original array and its nested objects
    const clonedScenes = _.clone(scenesWithSource);

    if (selectedSource) {
      dispatch(
        replaceSourceInScenes(
          sourceId,
          selectedSource,
          clonedScenes,
          deleteParams
        )
      );
      setSelectedSource(null);
    } else {
      dispatch(deleteSourceFromScene(sourceId, clonedScenes, deleteParams));
    }
    clear();
  };

  const filterOverlayOrBackgroundSources = deleteParams => {
    if (!isEmpty(deleteParams)) {
      if (deleteParams.isBackgroundSource) {
        return backgrounds.filter(b => b._id !== sourceId).map(s => s.source);
      } else if (deleteParams.isOverlaySource) {
        return overlays.filter(o => o._id !== sourceId).map(s => s.source);
      } else if (deleteParams.isBannerSource) {
        return banners
          .filter(
            b =>
              b._id !== sourceId &&
              b.source?.value?.templateType !== 'presenterBanner'
          )
          .map(s => s.source);
      }
    }
    return sources;
  };

  const filterSources = () => {
    const currentSources = filterOverlayOrBackgroundSources(deleteParams);
    const selectedSource = currentSources.find(
      s => sourceId && sourceId === s._id
    );
    if (selectedSource && isPresenterSource(selectedSource)) {
      return currentSources.filter(source => {
        return source._id !== sourceId && selectedSource.type === source.type;
      });
    }
    return currentSources.filter(source => {
      return (
        source._id !== sourceId &&
        (isMediaSource(source) || source.type === BANNER)
      );
    });
  };

  const sourceIsMedia = () => {
    const source = sources.find(source => source._id === sourceId);
    return source && isMediaSource(source);
  };

  const sourceIsScreenPresenter = () => {
    const source = sources.find(source => source._id === sourceId);
    return source && isScreenPresenter(source);
  };

  const getSourceType = () => {
    let sourceType = 'speaker';
    if (!isEmpty(deleteParams)) {
      if (deleteParams.isBackgroundSource) {
        sourceType = 'background';
      } else if (deleteParams.isOverlaySource) {
        sourceType = 'overlay';
      } else if (deleteParams.isBannerSource) {
        sourceType = 'banner';
      }
    } else if (sourceIsMedia()) {
      sourceType = 'content';
    } else if (sourceIsScreenPresenter()) {
      sourceType = 'screen share';
    }
    return sourceType;
  };

  if (!isOpen) return null;

  return (
    <StyledModal
      isOpen={isOpen}
      toggle={clear}
      data-testid="delete-source-modal"
    >
      <ModalHeader toggle={clear}>
        <StyledTitle>{`Heads up, this ${getSourceType()} is saved in a scene!`}</StyledTitle>
      </ModalHeader>
      <DeleteSource
        isBackgroundSource={deleteParams?.isBackgroundSource}
        onCancel={clear}
        scenes={scenesWithSource}
        sources={filterSources()}
        onSourceSelected={handleSourceSelected}
        onSubmit={handleSubmit}
        sourceType={getSourceType()}
      />
    </StyledModal>
  );
};

export default DeleteSourceModal;
